import { useState, useEffect, useCallback } from "react";

/**
 * useScrollToTop Hook
 *
 * Custom hook that manages scroll-to-top button visibility and provides
 * a smooth scroll function. Works with both window scroll and container scroll.
 *
 * **Features:**
 * - Tracks scroll position (window or container)
 * - Shows button when scrolled past threshold
 * - Provides smooth scroll-to-top function
 * - Auto cleanup on unmount
 * - Performance optimized with useCallback
 *
 * **Usage:**
 * ```tsx
 * const MyScreen = () => {
 *   const { showScrollTop, scrollToTop } = useScrollToTop(400);
 *
 *   return (
 *     <>
 *       {/* Screen content *\/}
 *       <ScrollToTopFab show={showScrollTop} onClick={scrollToTop} />
 *     </>
 *   );
 * };
 * ```
 *
 * @param threshold - Scroll distance in pixels before showing button (default: 400)
 * @returns Object containing showScrollTop boolean and scrollToTop function
 */
export const useScrollToTop = (threshold: number = 400) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Memoized scroll handler to prevent recreation on every render
  const handleScroll = useCallback(() => {
    // Check if we're in a scrollable container or using window scroll
    const mainContent = document.querySelector("main");
    let scrollY = 0;

    if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
      // Main content has its own scroll
      scrollY = mainContent.scrollTop;
    } else {
      // Use window scroll (for public pages like landing)
      scrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
    }

    console.log(
      "[useScrollToTop] Scroll position:",
      scrollY,
      "Threshold:",
      threshold
    );

    if (scrollY > threshold) {
      if (!showScrollTop) {
        console.log("[useScrollToTop] Showing button");
        setShowScrollTop(true);
      }
    } else {
      if (showScrollTop) {
        console.log("[useScrollToTop] Hiding button");
        setShowScrollTop(false);
      }
    }
  }, [threshold, showScrollTop]);

  // Memoized scroll-to-top function
  const scrollToTop = useCallback(() => {
    // Check if we're in a scrollable container or using window scroll
    const mainContent = document.querySelector("main");

    if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
      // Main content has its own scroll
      mainContent.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      // Use window scroll (for public pages like landing)
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // Setup scroll event listener
  useEffect(() => {
    // Get the scrollable element (main content or window)
    const mainContent = document.querySelector("main");
    let scrollElement: HTMLElement | Window = window;

    // Check if main content has its own scroll
    if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
      scrollElement = mainContent;
      console.log("[useScrollToTop] Using main content scroll");
    } else {
      console.log("[useScrollToTop] Using window scroll");
    }

    // Check initial scroll position on mount
    handleScroll();

    // Add scroll event listener
    scrollElement.addEventListener("scroll", handleScroll);

    // Cleanup on unmount
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return {
    /** Boolean indicating if scroll-to-top button should be visible */
    showScrollTop,
    /** Function to smoothly scroll to top of page */
    scrollToTop,
  };
};
